// src/hooks/useAuth.js
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileUnsubRef = useRef(null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up previous profile listener when auth state changes
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        const userRef = doc(db, 'users', firebaseUser.uid);
        const snap    = await getDoc(userRef);

        if (!snap.exists()) {
          // First sign-in: check for a pending invite to honour the pre-set role
          let role = 'staff';
          try {
            const inviteRef  = doc(db, 'pending_invites', firebaseUser.email.toLowerCase());
            const inviteSnap = await getDoc(inviteRef);
            if (inviteSnap.exists()) {
              role = inviteSnap.data().role || 'staff';
              await deleteDoc(inviteRef); // consume the invite
            }
          } catch (_) {}

          await setDoc(userRef, {
            uid:       firebaseUser.uid,
            name:      firebaseUser.displayName,
            email:     firebaseUser.email,
            photoURL:  firebaseUser.photoURL,
            role,
            createdAt: serverTimestamp(),
          });
        }

        // Subscribe to live profile updates so admin role changes reflect immediately
        profileUnsubRef.current = onSnapshot(userRef, (profileSnap) => {
          if (profileSnap.exists()) {
            setProfile({ uid: firebaseUser.uid, ...profileSnap.data() });
          }
          setLoading(false);
        });
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, []);

  const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
  const logOut           = () => signOut(auth);
  const isAdmin          = profile?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin, signInWithGoogle, logOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
